/**
 * Confirmation dialog for moving a favorite bookmark to a new location.
 */

import type { Bookmark } from '@skjv/common';
import { formatShortReference } from '@skjv/common';
import { StarIcon } from '@components/icons';

interface BookmarkMoveConfirmProps {
  bookmark: Bookmark;
  targetReference: string;
  animationState: 'entering' | 'exiting' | 'visible';
  onConfirm: () => void;
  onCancel: () => void;
}

export function BookmarkMoveConfirm({
  bookmark,
  targetReference,
  animationState,
  onConfirm,
  onCancel,
}: BookmarkMoveConfirmProps) {
  const oldRef = formatShortReference(bookmark);

  return (
    <>
      {/* Overlay */}
      <div className={`bookmark-creator-overlay ${animationState}`} onClick={onCancel} />

      {/* Confirmation Modal */}
      <div
        className={`bookmark-creator ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''}`}
      >
        <h3 className="mb-3 text-xl font-semibold text-stone-800 dark:text-stone-200">
          Move Bookmark?
        </h3>

        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-700/30 dark:bg-amber-900/20">
          <div className="mb-2 flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-amber-500" filled />
            <span className="font-medium text-stone-800 dark:text-stone-200">
              {bookmark.name}
            </span>
          </div>
          <div className="text-sm text-stone-600 dark:text-stone-400">
            <span className="line-through opacity-60">{oldRef}</span>
            <span className="mx-2">&rarr;</span>
            <span className="font-medium text-cyan-600 dark:text-cyan-400">
              {targetReference}
            </span>
          </div>
        </div>

        <p className="mb-4 text-sm text-stone-600 dark:text-stone-400">
          This will update "{bookmark.name}" to point to {targetReference}.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-xl bg-stone-200/50 py-3 text-lg font-medium text-stone-600 transition-all hover:bg-stone-300/70 dark:bg-stone-700/30 dark:text-stone-400 dark:hover:bg-stone-600/40"
          >
            Cancel
          </button>
          <button onClick={onConfirm} className="bookmark-save-btn flex-1">
            Move
          </button>
        </div>
      </div>
    </>
  );
}
